import { Flex } from "@mantine/core";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Text } from "components";

interface BankCreditRequestHeaderProps {
  creditAmount?: number;
  paymentPeriod?: number;
  monthlyInstallmentFrom?: number;
  monthlyInstallmentTo?: number;
  isExpanded: boolean;
  onToggle: () => void;
}

export const BankCreditRequestHeader = ({
  creditAmount,
  paymentPeriod,
  monthlyInstallmentFrom,
  monthlyInstallmentTo,
  isExpanded,
  onToggle,
}: BankCreditRequestHeaderProps) => {
  const { t } = useTranslation();
  const valueTextProps = { size: "sm", fw: 500 } as const;

  const formatCurrency = (value: number) =>
    value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const renderMonthlyInstallment = () => {
    const installmentFrom = monthlyInstallmentFrom;
    const installmentTo = monthlyInstallmentTo;

    if (installmentFrom == null && installmentTo == null) {
      return null;
    }

    if (installmentFrom != null && installmentTo != null) {
      const formattedValue =
        installmentFrom === installmentTo
          ? `${formatCurrency(installmentFrom)} CHF`
          : `${formatCurrency(installmentFrom)} - ${formatCurrency(installmentTo)} CHF`;

      return (
        <Text {...valueTextProps}>
          {formattedValue}
        </Text>
      );
    }

    const installmentValue = installmentFrom ?? installmentTo;

    return (
      <Text {...valueTextProps}>
        {`${formatCurrency(installmentValue!)} CHF`}
      </Text>
    );
  };

  const headerItems = [
    {
      label: t("bankCredit.header.info.creditAmount"),
      hasValue: creditAmount != null,
      formatValue: () => (
        <Text {...valueTextProps}>
          {`${formatCurrency(creditAmount!)} CHF`}
        </Text>
      ),
    },
    {
      label: t("bankCredit.header.info.repaymentTerm"),
      hasValue: paymentPeriod != null,
      formatValue: () => (
        <Text {...valueTextProps}>
          {paymentPeriod}
        </Text>
      ),
    },
    {
      label: t("bankCredit.header.info.monthlyInstallment"),
      hasValue: monthlyInstallmentFrom != null || monthlyInstallmentTo != null,
      formatValue: renderMonthlyInstallment,
    },
  ];

  return (
    <Flex
      direction="row"
      gap="md"
      mb={"md"}
      align="center"
      bg={"var(--mantine-color-gray-1)"}
      w={"100%"}
      style={{ borderRadius: 20 }}
    >
      <IconCaretDownFilled
        size={20}
        color="black"
        onClick={onToggle}
        style={{
          cursor: "pointer",
          transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
          transition: "transform 0.3s ease",
          marginLeft: 12,
        }}
      />

      <Flex direction="column">
        <Text size="lg" fw={500} c="#B046AC">
          {t("bankCredit.header.title")}
        </Text>

        <Flex direction="row" gap="md" align="center">
          {headerItems.map(({ hasValue, label, formatValue }, index) => (
            <Flex key={index} direction="row" gap={4} align="center">
              <Text size="sm" c="black">
                {`${label}:`}
              </Text>
              {hasValue ? (
                formatValue()
              ) : (
                <Text size="md" fw={500}>
                  _ _ _
                </Text>
              )}
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
};
